import React, { useCallback, useEffect, useState } from 'react';
import { getAllProjectsReview, getProjectBreakdown } from '../../api/reportsApi';
import { usePermissions } from '../PermissionsContext';
import './Reports.css';

const barClass = (consumed) => (consumed > 100 ? 'red' : consumed >= 90 ? 'amber' : 'green');
const fmtH = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
const fmtMoney = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;

const ConsumedBar = ({ consumed }) => (
  <div className="bar-wrap">
    <div className="bar"><div className={`bar-fill ${barClass(consumed)}`} style={{ width: `${Math.min(consumed, 100)}%` }} /></div>
    <span className="bar-text">{fmtPct(consumed)}</span>
  </div>
);

export default function AllProjectsReviewPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const allowed = hasPermission?.('Timesheet.ViewProjectReports');

  const [status, setStatus] = useState('Active');
  const [period, setPeriod] = useState('ProjectToDate');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});      // projectId → true
  const [breakdowns, setBreakdowns] = useState({});  // projectId → ProjectPlanBreakdownDto

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dto = await getAllProjectsReview(status, period);
      setData(dto);
      setExpanded({});
      setBreakdowns({});
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load projects review.');
    } finally {
      setLoading(false);
    }
  }, [status, period]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const toggleRow = async (projectId) => {
    const willOpen = !expanded[projectId];
    setExpanded((prev) => ({ ...prev, [projectId]: willOpen }));
    if (willOpen && !breakdowns[projectId]) {
      try {
        const bd = await getProjectBreakdown(projectId, period);
        setBreakdowns((prev) => ({ ...prev, [projectId]: bd }));
      } catch {
        setBreakdowns((prev) => ({ ...prev, [projectId]: { rows: [], error: true } }));
      }
    }
  };

  if (!permLoading && !allowed) {
    return <div className="reports-page"><p className="empty-note">You don't have access to project reports.</p></div>;
  }

  const projects = data?.projects || [];
  const marginColor = (data?.totalMarginPercent ?? 0) >= 40 ? 'green' : (data?.totalMarginPercent ?? 0) >= 25 ? 'amber' : 'red';
  const consumedColor = (data?.totalConsumedPercent ?? 0) > 100 ? 'red' : (data?.totalConsumedPercent ?? 0) >= 90 ? 'amber' : 'green';

  return (
    <div className="reports-page" data-testid="all-projects-review">
      <div className="breadcrumb">Reports · All Projects Review</div>
      <h1 className="page-title">All Projects Review</h1>
      <p className="page-sub">Aggregated planned vs actual figures. Click a row to expand the project plan breakdown.</p>

      <div className="filter-bar">
        <label htmlFor="apr-period">Period</label>
        <select id="apr-period" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="apr-period">
          <option value="ProjectToDate">Project to date</option>
          <option value="ThisMonth">This month</option>
        </select>
        <label htmlFor="apr-status">Project status</label>
        <select id="apr-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="apr-status">
          <option value="All">All</option>
          <option value="Active">Active only</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {error && <div className="report-error">{error}</div>}

      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-label">Active Projects</div>
          <div className="kpi-value">{data?.activeProjectCount ?? 0}</div>
          <div className="kpi-sub">of {data?.totalProjectCount ?? 0} total</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Planned Hours</div>
          <div className="kpi-value">{fmtH(data?.totalPlanHours)}</div>
        </div>
        <div className={`kpi ${consumedColor}`}>
          <div className="kpi-label">Actual Hours</div>
          <div className="kpi-value">{fmtH(data?.totalActualHours)}</div>
          <div className="kpi-sub">{fmtPct(data?.totalConsumedPercent)} consumed</div>
        </div>
        <div className={`kpi ${marginColor}`}>
          <div className="kpi-label">Margin</div>
          <div className="kpi-value">{data?.totalMarginPercent == null ? '—' : fmtPct(data.totalMarginPercent)}</div>
        </div>
        {/* RA-S3 — billable WIs with no ProjectRoleId. These hours count
            as actual but sit outside every (task, role) row until a PM
            picks a role. The KPI surfaces the backlog so it doesn't
            silently distort actual-vs-plan reads. */}
        {data?.totalNoRoleWiCount > 0 && (
          <div className="kpi" data-testid="apr-noroleroles-kpi" style={{ background: '#fff3c2', color: '#856404' }}>
            <div className="kpi-label">Unattributed WIs</div>
            <div className="kpi-value">{data.totalNoRoleWiCount}</div>
            <div className="kpi-sub">billable WIs need a role</div>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-header"><h3 className="panel-title">Projects (click to expand)</h3></div>
        {loading ? (
          <div className="empty-note">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="empty-note">No projects match the selected filter.</div>
        ) : (
          <table className="cells" data-testid="apr-table">
            <thead>
              <tr>
                <th></th><th>Project</th><th>PM</th>
                <th className="num">Plan h</th><th className="num">Actual h</th><th className="num">Consumed</th>
                <th className="num">Sell (€)</th><th className="num">Cost (€)</th><th className="num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <React.Fragment key={p.projectId}>
                  <tr className="clickable" onClick={() => toggleRow(p.projectId)} data-testid={`apr-row-${p.projectId}`}>
                    <td style={{ width: 24 }}>
                      <span className={`expand-arrow${expanded[p.projectId] ? ' open' : ''}`}>▶</span>
                    </td>
                    <td>
                      <strong>{p.projectName}</strong>
                      {p.noRoleWiCount > 0 && (
                        <span
                          data-testid={`apr-norole-${p.projectId}`}
                          title={`${p.noRoleWiCount} billable work item${p.noRoleWiCount === 1 ? '' : 's'} without a role — cost = 0 until a PM picks one`}
                          style={{
                            marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                            background: '#fff3c2', color: '#856404',
                            border: '1px solid #e6c34a', fontSize: 11, fontWeight: 600,
                          }}
                        >
                          {p.noRoleWiCount} no-role
                        </span>
                      )}
                    </td>
                    <td>{p.pmName || '—'}</td>
                    <td className="num">{fmtH(p.planHours)}</td>
                    <td className="num">{fmtH(p.actualHours)}</td>
                    <td className="num"><ConsumedBar consumed={p.consumedPercent} /></td>
                    <td className="num">{p.isBillable ? fmtMoney(p.sell) : '—'}</td>
                    <td className="num">{fmtMoney(p.cost)}</td>
                    <td className="num">{p.marginPercent == null ? '—' : fmtPct(p.marginPercent)}</td>
                  </tr>
                  {expanded[p.projectId] && (
                    <tr className="expanded-content" data-testid={`apr-expansion-${p.projectId}`}>
                      <td colSpan={9}>
                        <BreakdownTable name={p.projectName} bd={breakdowns[p.projectId]} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="total-row">
                <td></td><td colSpan={2}>Total ({status === 'Active' ? 'Active' : status})</td>
                <td className="num">{fmtH(data?.totalPlanHours)}</td>
                <td className="num">{fmtH(data?.totalActualHours)}</td>
                <td className="num">{fmtPct(data?.totalConsumedPercent)}</td>
                <td className="num">{fmtMoney(data?.totalSell)}</td>
                <td className="num">{fmtMoney(data?.totalCost)}</td>
                <td className="num">{data?.totalMarginPercent == null ? '—' : fmtPct(data.totalMarginPercent)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BreakdownTable({ name, bd }) {
  if (!bd) return <div className="empty-note">Loading breakdown…</div>;
  if (bd.error) return <div className="empty-note">Failed to load breakdown.</div>;
  const rows = bd.rows || [];
  return (
    <>
      <div className="breakdown-caption"><strong>{name}</strong> — plan breakdown by phase and role</div>
      <table className="cells">
        <thead>
          <tr>
            <th>Phase</th><th>Role</th>
            <th className="num">Plan h</th><th className="num">Actual h</th><th className="num">Consumed</th>
            <th className="num">Sell (€)</th><th className="num">Cost (€)</th><th className="num">Margin</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="empty-note">No plan rows.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={`${r.phaseTaskId}-${r.projectRoleId}-${i}`}>
              <td>{r.phaseName}</td>
              <td>{r.roleName}</td>
              <td className="num">{fmtH(r.planHours)}</td>
              <td className="num">{fmtH(r.actualHours)}</td>
              <td className="num">{fmtPct(r.consumedPercent)}</td>
              <td className="num">{r.marginPercent == null ? '—' : fmtMoney(r.sell)}</td>
              <td className="num">{fmtMoney(r.cost)}</td>
              <td className="num">{r.marginPercent == null ? '—' : fmtPct(r.marginPercent)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="total-row">
              <td colSpan={2}>Project total</td>
              <td className="num">{fmtH(bd.totalPlanHours)}</td>
              <td className="num">{fmtH(bd.totalActualHours)}</td>
              <td className="num">{fmtPct(bd.totalConsumedPercent)}</td>
              <td className="num">{bd.totalMarginPercent == null ? '—' : fmtMoney(bd.totalSell)}</td>
              <td className="num">{fmtMoney(bd.totalCost)}</td>
              <td className="num">{bd.totalMarginPercent == null ? '—' : fmtPct(bd.totalMarginPercent)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
